import React from 'react';
import { decimalToAmerican, formatGameTime, sportHasDraws, BET_LIMITS, PROPS_SPORTS } from '../../constants/sports';

function GameOddsCard({ game, sportKey, betSlipLegs, onToggleLeg, onViewProps, disabled }) {
  const { id, homeTeam, awayTeam, homeAbbrev, awayAbbrev, homeLogo, awayLogo, commenceTime, markets, status, homeScore, awayScore } = game;
  const hasDraw = sportHasDraws(sportKey);
  const isCompleted = status === 'completed';
  const isLive = status === 'in_progress';

  // Client-side betting close check (matches server logic)
  const closingSoon = !isCompleted && !isLive && status === 'scheduled' &&
    (commenceTime - Date.now()) < BET_LIMITS.BETTING_CLOSE_MINUTES * 60 * 1000;
  const effectiveDisabled = disabled || closingSoon;

  const isLegSelected = (market, selection) => {
    return betSlipLegs.some(l => l.gameId === id && l.market === market && l.selection === selection);
  };

  const handleOddsClick = (market, selection, odds, point) => {
    if (effectiveDisabled) return;
    onToggleLeg({
      gameId: id,
      sport: sportKey,
      homeTeam,
      awayTeam,
      commenceTime,
      market,
      selection,
      odds,
      point: point || null,
    });
  };

  const renderOddsButton = (market, selection, odds, point, label) => {
    if (!odds) return <div className="odds-btn odds-btn-empty">-</div>;
    const selected = isLegSelected(market, selection);
    return (
      <button
        className={`odds-btn ${selected ? 'odds-btn-selected' : ''} ${effectiveDisabled ? 'odds-btn-disabled' : ''}`}
        onClick={() => handleOddsClick(market, selection, odds, point)}
        disabled={effectiveDisabled}
      >
        {label && <span className="odds-label">{label}</span>}
        <span className="odds-value">{decimalToAmerican(odds)}</span>
      </button>
    );
  };

  return (
    <div className={`game-odds-card ${isCompleted ? 'game-completed' : ''} ${isLive ? 'game-live' : ''}`}>
      <div className="game-odds-teams">
        <div className="game-time">
          {isLive && <span className="game-live-badge">LIVE</span>}
          {isCompleted ? 'Final' : closingSoon ? 'Starting Soon' : formatGameTime(commenceTime)}
          {PROPS_SPORTS.includes(sportKey) && !isCompleted && !effectiveDisabled && onViewProps && (
            <span className="game-props-link" onClick={(e) => { e.stopPropagation(); onViewProps(game); }}>Props</span>
          )}
        </div>
        <div className="game-team">
          {awayLogo && <img src={awayLogo} alt="" className="team-logo" />}
          <span className="team-name">{awayTeam}</span>
          <span className="team-abbrev">{awayAbbrev}</span>
          {(isCompleted || isLive) && <span className="team-score">{awayScore}</span>}
        </div>
        <div className="game-team">
          {homeLogo && <img src={homeLogo} alt="" className="team-logo" />}
          <span className="team-name">{homeTeam}</span>
          <span className="team-abbrev">{homeAbbrev}</span>
          {(isCompleted || isLive) && <span className="team-score">{homeScore}</span>}
        </div>
      </div>

      {markets && (
        <div className="game-odds-markets">
          {/* Moneyline */}
          <div className="game-odds-column">
            <div className="market-header">ML</div>
            {markets.h2h ? (
              <>
                {renderOddsButton('h2h', 'away', markets.h2h.away)}
                {renderOddsButton('h2h', 'home', markets.h2h.home)}
                {hasDraw && markets.h2h.draw && renderOddsButton('h2h', 'draw', markets.h2h.draw, null, 'Draw')}
              </>
            ) : (
              <>
                <div className="odds-btn odds-btn-empty">-</div>
                <div className="odds-btn odds-btn-empty">-</div>
              </>
            )}
          </div>

          {/* Spread */}
          <div className="game-odds-column">
            <div className="market-header">Spread</div>
            {markets.spreads ? (
              <>
                {renderOddsButton('spreads', 'away', markets.spreads.away.price, markets.spreads.away.point,
                  `${markets.spreads.away.point > 0 ? '+' : ''}${markets.spreads.away.point}`)}
                {renderOddsButton('spreads', 'home', markets.spreads.home.price, markets.spreads.home.point,
                  `${markets.spreads.home.point > 0 ? '+' : ''}${markets.spreads.home.point}`)}
              </>
            ) : (
              <>
                <div className="odds-btn odds-btn-empty">-</div>
                <div className="odds-btn odds-btn-empty">-</div>
              </>
            )}
          </div>

          {/* Totals */}
          <div className="game-odds-column">
            <div className="market-header">O/U</div>
            {markets.totals ? (
              <>
                {renderOddsButton('totals', 'over', markets.totals.over.price, markets.totals.over.point,
                  `O ${markets.totals.over.point}`)}
                {renderOddsButton('totals', 'under', markets.totals.under.price, markets.totals.under.point,
                  `U ${markets.totals.under.point}`)}
              </>
            ) : (
              <>
                <div className="odds-btn odds-btn-empty">-</div>
                <div className="odds-btn odds-btn-empty">-</div>
              </>
            )}
          </div>
        </div>
      )}

      {!markets && !isCompleted && !isLive && (
        <div className="game-no-odds">Odds not available</div>
      )}

    </div>
  );
}

export default GameOddsCard;
